import { create } from "zustand";
import { persist } from "zustand/middleware";
import { useAuthStore } from "../auth/authStore";
import {
  deleteCurveWorkspaceRemote,
  loadCurveWorkspaceRemote,
  saveCurveWorkspaceRemote,
} from "../api/curveWorkspacesRepo";
import {
  createVolWorkstationLayout,
  deleteVolWorkstationLayout,
  listVolWorkstationLayouts,
  updateVolWorkstationLayout,
  type VolWorkstationPublicLayout,
} from "../api/volWorkstationLayoutsRepo";
import type {
  VolWidgetInstance,
  VolWidgetKind,
  VolWorkspaceDocument,
  VolWorkspaceTab,
} from "../modules/curves/workspace/volWorkstationTypes";
import { VOL_WORKSPACE_SCHEMA_VERSION } from "../modules/curves/workspace/volWorkstationTypes";
import { VOL_WIDGET_LAYOUT_DEFAULTS } from "../modules/curves/workspace/volWidgetDefaults";
import { CORRELATION_WIDGET_DEFAULTS } from "../widgets/correlation/correlationWidgetDefaults";
import { isCorrelationWidgetKind } from "../widgets/correlation/correlationWidgetTypes";

export type CurveWorkspaceBundle = {
  workspaces: VolWorkspaceTab[];
  currentId: string;
  syncMode: "local" | "remote";
  lastSyncedAt: number | null;
};

type VolWorkstationStoreState = {
  bundles: Record<string, CurveWorkspaceBundle>;
  sharedLayout: VolWorkspaceDocument | null;
  getBundle: (curveId: string) => CurveWorkspaceBundle;
  hydrate: (curveId: string) => Promise<void>;
  setCurrent: (curveId: string, tabId: string) => void;
  upsertLayout: (curveId: string, tabId: string, widgets: VolWidgetInstance[]) => void;
  addWidget: (curveId: string, kind: VolWidgetKind) => void;
  removeWidget: (curveId: string, widgetId: string) => void;
  newWorkspace: (curveId: string, name: string) => void;
  deleteWorkspace: (curveId: string, tabId: string) => void;
  renameWorkspace: (curveId: string, tabId: string, name: string) => void;
  resetToSeed: (curveId: string) => void;
  importDocument: (curveId: string, doc: VolWorkspaceDocument) => void;
  exportDocument: (curveId: string) => VolWorkspaceDocument;
  applyLayoutDocument: (curveId: string, doc: VolWorkspaceDocument, updateShared?: boolean) => void;
  listPublicLayouts: (scopeCurveId?: string) => Promise<VolWorkstationPublicLayout[]>;
  savePublicLayout: (
    curveId: string,
    name: string,
    isPublic?: boolean,
    layoutId?: string,
  ) => Promise<VolWorkstationPublicLayout>;
  loadPublicLayout: (curveId: string, layoutId: string) => Promise<void>;
  deletePublicLayout: (layoutId: string, createdBy: string, isAdmin: boolean) => Promise<void>;
};

function newWidgetId(): string {
  return `w-${Math.random().toString(36).slice(2, 10)}`;
}

function makeWidget(
  kind: VolWidgetKind,
  pos?: Partial<Pick<VolWidgetInstance, "x" | "y" | "w" | "h">>,
): VolWidgetInstance {
  if (isCorrelationWidgetKind(kind)) {
    const spec = CORRELATION_WIDGET_DEFAULTS[kind];
    return {
      id: newWidgetId(),
      kind,
      x: pos?.x ?? 0,
      y: pos?.y ?? 0,
      w: pos?.w ?? spec.w,
      h: pos?.h ?? spec.h,
    };
  }
  const spec = VOL_WIDGET_LAYOUT_DEFAULTS[kind];
  return {
    id: newWidgetId(),
    kind,
    x: pos?.x ?? spec.x ?? 0,
    y: pos?.y ?? spec.y ?? 0,
    w: pos?.w ?? spec.w,
    h: pos?.h ?? spec.h,
  };
}

export function seedVolWorkspaceTabs(): VolWorkspaceTab[] {
  return [
    {
      id: "default",
      name: "Default",
      isDefault: true,
      widgets: [
        makeWidget("vol-surface-3d", { x: 0, y: 0, w: 14, h: 24 }),
        makeWidget("vol-term", { x: 14, y: 0, w: 10, h: 12 }),
        makeWidget("vol-skew", { x: 14, y: 12, w: 10, h: 12 }),
        makeWidget("vol-calibration", { x: 0, y: 24, w: 16, h: 18 }),
        makeWidget("vol-controls", { x: 16, y: 24, w: 8, h: 8 }),
      ],
    },
  ];
}

function seedBundle(): CurveWorkspaceBundle {
  const workspaces = seedVolWorkspaceTabs();
  return {
    workspaces,
    currentId: workspaces[0]!.id,
    syncMode: "local",
    lastSyncedAt: null,
  };
}

function toDocument(bundle: CurveWorkspaceBundle): VolWorkspaceDocument {
  return {
    schemaVersion: VOL_WORKSPACE_SCHEMA_VERSION,
    currentId: bundle.currentId,
    workspaces: bundle.workspaces,
  };
}

function fromDocument(doc: VolWorkspaceDocument, syncMode: "local" | "remote" = "remote"): CurveWorkspaceBundle {
  const workspaces = doc.workspaces?.length > 0 ? doc.workspaces : seedVolWorkspaceTabs();
  const currentId =
    workspaces.find((w) => w.id === doc.currentId)?.id ??
    workspaces.find((w) => w.isDefault)?.id ??
    workspaces[0]!.id;
  return {
    workspaces,
    currentId,
    syncMode,
    lastSyncedAt: syncMode === "remote" ? Date.now() : null,
  };
}

function fallbackBundle(get: () => VolWorkstationStoreState): CurveWorkspaceBundle {
  const shared = get().sharedLayout;
  if (shared) return fromDocument(shared, "local");
  return seedBundle();
}

const pendingFlush: Record<string, ReturnType<typeof setTimeout> | null> = {};

function scheduleFlush(curveId: string, get: () => VolWorkstationStoreState) {
  const bundle = get().bundles[curveId];
  if (!bundle || bundle.syncMode !== "remote") return;
  if (pendingFlush[curveId]) clearTimeout(pendingFlush[curveId]!);
  pendingFlush[curveId] = setTimeout(async () => {
    pendingFlush[curveId] = null;
    const token = useAuthStore.getState().accessToken;
    if (!token) return;
    const b = get().bundles[curveId];
    if (!b) return;
    try {
      await saveCurveWorkspaceRemote(curveId, token, toDocument(b));
      useVolWorkstationStore.setState((s) => ({
        bundles: {
          ...s.bundles,
          [curveId]: { ...s.bundles[curveId]!, lastSyncedAt: Date.now() },
        },
      }));
    } catch {
      /* retry on next mutation */
    }
  }, 800);
}

function patchBundle(
  curveId: string,
  patch: (b: CurveWorkspaceBundle) => CurveWorkspaceBundle,
  get: () => VolWorkstationStoreState,
) {
  const prev = get().bundles[curveId] ?? fallbackBundle(get);
  const next = patch(prev);
  useVolWorkstationStore.setState((s) => ({
    bundles: { ...s.bundles, [curveId]: next },
    sharedLayout: toDocument(next),
  }));
  scheduleFlush(curveId, get);
}

export const useVolWorkstationStore = create<VolWorkstationStoreState>()(
  persist(
    (set, get) => ({
      bundles: {},
      sharedLayout: null,

      getBundle: (curveId) => get().bundles[curveId] ?? fallbackBundle(get),

      hydrate: async (curveId) => {
        const token = useAuthStore.getState().accessToken;
        const localFallback = get().bundles[curveId] ?? fallbackBundle(get);

        if (!token) {
          set((s) => ({
            bundles: { ...s.bundles, [curveId]: localFallback },
          }));
          return;
        }

        try {
          const remote = await loadCurveWorkspaceRemote(curveId, token);
          if (remote) {
            set((s) => ({
              bundles: { ...s.bundles, [curveId]: fromDocument(remote) },
              sharedLayout: s.sharedLayout ?? remote,
            }));
            return;
          }
        } catch {
          /* fall through */
        }

        set((s) => ({
          bundles: {
            ...s.bundles,
            [curveId]: {
              ...localFallback,
              syncMode: "remote",
            },
          },
        }));
        scheduleFlush(curveId, get);
      },

      applyLayoutDocument: (curveId, doc, updateShared = true) => {
        const bundle = fromDocument(doc);
        set((s) => ({
          bundles: { ...s.bundles, [curveId]: bundle },
          sharedLayout: updateShared ? doc : s.sharedLayout,
        }));
        scheduleFlush(curveId, get);
      },

      setCurrent: (curveId, tabId) => {
        patchBundle(curveId, (b) => ({ ...b, currentId: tabId }), get);
      },

      upsertLayout: (curveId, tabId, widgets) => {
        patchBundle(
          curveId,
          (b) => ({
            ...b,
            workspaces: b.workspaces.map((w) => (w.id === tabId ? { ...w, widgets } : w)),
          }),
          get,
        );
      },

      addWidget: (curveId, kind) => {
        patchBundle(
          curveId,
          (b) => ({
            ...b,
            workspaces: b.workspaces.map((w) =>
              w.id === b.currentId ? { ...w, widgets: [...w.widgets, makeWidget(kind)] } : w,
            ),
          }),
          get,
        );
      },

      removeWidget: (curveId, widgetId) => {
        patchBundle(
          curveId,
          (b) => ({
            ...b,
            workspaces: b.workspaces.map((w) =>
              w.id === b.currentId ? { ...w, widgets: w.widgets.filter((x) => x.id !== widgetId) } : w,
            ),
          }),
          get,
        );
      },

      newWorkspace: (curveId, name) => {
        const id = `ws-${Math.random().toString(36).slice(2, 9)}`;
        patchBundle(
          curveId,
          (b) => ({
            ...b,
            currentId: id,
            workspaces: [...b.workspaces, { id, name, isDefault: false, widgets: [] }],
          }),
          get,
        );
      },

      deleteWorkspace: (curveId, tabId) => {
        patchBundle(
          curveId,
          (b) => {
            const remaining = b.workspaces.filter((w) => w.id !== tabId);
            if (!remaining.length) return b;
            const nextCur = b.currentId === tabId ? remaining[0]!.id : b.currentId;
            return { ...b, workspaces: remaining, currentId: nextCur };
          },
          get,
        );
      },

      renameWorkspace: (curveId, tabId, name) => {
        patchBundle(
          curveId,
          (b) => ({
            ...b,
            workspaces: b.workspaces.map((w) => (w.id === tabId ? { ...w, name } : w)),
          }),
          get,
        );
      },

      resetToSeed: (curveId) => {
        const seeded = seedBundle();
        seeded.syncMode = get().bundles[curveId]?.syncMode ?? "local";
        set((s) => ({
          bundles: { ...s.bundles, [curveId]: seeded },
          sharedLayout: toDocument(seeded),
        }));
        const token = useAuthStore.getState().accessToken;
        if (token) {
          void deleteCurveWorkspaceRemote(curveId, token).catch(() => {});
        }
        scheduleFlush(curveId, get);
      },

      importDocument: (curveId, doc) => {
        get().applyLayoutDocument(curveId, doc, true);
      },

      exportDocument: (curveId) => toDocument(get().getBundle(curveId)),

      listPublicLayouts: (scopeCurveId) => {
        const token = useAuthStore.getState().accessToken;
        return listVolWorkstationLayouts(token, scopeCurveId);
      },

      savePublicLayout: async (curveId, name, isPublic = true, layoutId) => {
        const token = useAuthStore.getState().accessToken;
        const doc = get().exportDocument(curveId);
        if (layoutId) {
          return updateVolWorkstationLayout(token, layoutId, {
            name,
            data: doc,
            is_public: isPublic,
          });
        }
        return createVolWorkstationLayout(token, {
          name,
          scope_curve_id: curveId,
          data: doc,
          is_public: isPublic,
        });
      },

      loadPublicLayout: async (curveId, layoutId) => {
        const layouts = await get().listPublicLayouts();
        const found = layouts.find((l) => l.layout_id === layoutId);
        if (!found) throw new Error("layout not found");
        get().applyLayoutDocument(curveId, found.data, true);
      },

      deletePublicLayout: async (layoutId, createdBy, isAdmin) => {
        const userId = useAuthStore.getState().user?.userId ?? useAuthStore.getState().user?.username ?? "";
        if (!isAdmin && createdBy !== userId) {
          throw new Error("forbidden");
        }
        const token = useAuthStore.getState().accessToken;
        await deleteVolWorkstationLayout(token, layoutId);
      },
    }),
    {
      name: "atlas.vol-workspace.v1",
      partialize: (s) => ({ bundles: s.bundles, sharedLayout: s.sharedLayout }),
    },
  ),
);
